import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import intlShape from '../../../schemas/intl';
import Alert from '../../../../components/atoms/Alert';
import {
  sendEvent,
  isLatestEventLabel,
} from '../../../utils/gtm';
import messages from '../../../utils/messages';
import SubmitBarFigures from '../Teleporter/SubmitBarFigures';
import getElementOffsetPosition from '../../../utils/get-element-offset-position';
import style from './style';
import devices from '../../../styles/Devices';
import { brand } from '../../../localizations/current-locale';
import { alertSlack } from '../../../utils/API';
import store from '../../../store';

const omalaithos = brand === 'omalaina' || 'rahalaitos';

const AlertWrapper = styled.div`
  && {
    margin-top: 2rem;

    ${(brand === omalaithos) && css`
  width: 100%;
  margin: 30px auto;

  @media ${devices.upFromLargeTablet} {
    ${(brand === 'omalaina') && css`
      max-width: 67%;
    `}
    ${(brand === 'rahalaitos') && css`
      max-width: 85%;
    `}
  }
`}
}
`;

const TeleButton = styled.button`
  && {
    border: none;
    cursor: pointer;
    line-height: 0;
    text-align: center;
    text-transform: uppercase;
    font-weight: bold;
    height: ${style.buttonHeight};
    box-shadow: ${style.boxShadow};
    padding: ${style.buttonPadding};
    margin-top: ${style.marginTop};
    color: ${style.buttonTextColor};
    width: ${style.buttonWidth};
    background: ${style.ctaButtonBackground};
    border-radius: ${style.buttonBorderRadius};
    margin-top: ${style.buttonMarginTop};

    @media ${devices.upFromTablet} {
      margin-top: ${style.buttonMarginTop};
    }

    &:disabled {
      opacity: 0.6;
      pointer-events: none;
      background: ${style.disabledBg};
      color: ${style.disabledColor};
    }
  }
`;

const SubmitButton = ({
  disableButton,
  submitSuccess,
  data,
  intl: { formatMessage },
  setShowErrors,
}) => {
  const formState = useSelector((state) => state.form.application);
  const hasSentSlackAlert = useRef(false);

  const {
    submitting,
    submitFailed,
    syncErrors,
    error,
  } = formState;

  const applicantIsPoliticallyExposedPerson = formState.values.applicant
  && formState.values.applicant.politicallyExposedPerson;
  const coApplicantIsPoliticallyExposedPerson = formState.hasCoApplicant
  && formState.values.coApplicant && formState.values.coApplicant.politicallyExposedPerson;

  const customerIsPEP = applicantIsPoliticallyExposedPerson || coApplicantIsPoliticallyExposedPerson; // eslint-disable-line

  const uncaughtRuntimeError = (
    error
    && error.message?.id === data.messages.uncaughtSubmitError.id
  ) || false;

  let submitError = null;
  const generalSubmitError = {
    id: messages.generalSubmitError.id,
    type: 'neutral',
    message: formatMessage(messages.generalSubmitError, {
      a: (chunks) => <a href="tel:0770220180">{chunks}</a>,
      i: (chunks) => <i>{chunks}</i>,
      phoneNumber: '0770 22 01 80',
      customerPhoneNumber: formState.values.applicant?.phoneNumber,
    }),
  };

  if (customerIsPEP) {
    submitError = {
      id: messages.applicationFormPoliticallyExposedPersonWarning.id,
      message: formatMessage(messages.applicationFormPoliticallyExposedPersonWarning),
    };
  } else if (error) {
    if (error.message.id === messages.generalSubmitError.id) {
      submitError = generalSubmitError;
    } else if (uncaughtRuntimeError) {
      submitError = {
        id: messages.uncaughtSubmitError.id,
        type: 'neutral',
        message: formatMessage(messages.uncaughtSubmitError, {
          i: (chunks) => <i>{chunks}</i>,
          customerPhoneNumber: formState.values?.applicant?.phoneNumber,
        }),
      };
    } else {
      submitError = {
        id: error.message.id,
        message: {
          message: error.message,
          values: error.values,
        },
      };
    }
  } else if (syncErrors && submitFailed) {
    submitError = {
      id: messages.generalValidationError.id,
      message: formatMessage(messages.generalValidationError),
    };
  } else if (!syncErrors && submitFailed) {
    submitError = generalSubmitError;
  }

  const [scrolledError, setScrolledError] = useState(false);

  useEffect(() => {
    setScrolledError(submitError?.id);
    if (submitError && scrolledError !== submitError.id) {
      const errorElement = getElementOffsetPosition(window.document.querySelector('#error,#neutral'));
      window.scrollTo({
        top: errorElement.top - 175,
      });
    }
  }, [submitError, scrolledError]);

  if (submitError?.id && submitFailed && !submitting && !isLatestEventLabel(submitError.id)) {
    sendEvent({
      event: 'Error',
      eventCategory: 'form-interaction',
      eventAction: 'submit-failure-submit-button',
      eventLabel: submitError.id,
    });
  }

  useEffect(() => {
    if (
      submitError
      && submitFailed
      && !submitting
      && !hasSentSlackAlert.current
      && !submitError?.message?.message?.id?.endsWith('submitError.duplicateApplication')
    ) {
      hasSentSlackAlert.current = true;
      const href = typeof window !== 'undefined' && window.location ? window.location.href : 'unknown location';
      const state = store.getState();
      const slide = state?.form?.genericFormValues?.currentSlide;
      const slackAlertText = `uncaught submitError occurred at slide ${slide} on : ${href}`;
      alertSlack(slackAlertText);
    }
  }, [submitError, submitFailed, uncaughtRuntimeError, submitting]);

  return (
    <div
      className="sumbit-bar-anchor"
      onClick={() => (disableButton
        ? setShowErrors(true)
        : setShowErrors(false))}
      role="button"
      tabIndex={0}
    >
      <SubmitBarFigures />
      <TeleButton
        type="submit"
        className={classNames({ 'c-button--loading': submitting || submitSuccess },
        )}
        disabled={
        submitting
        || disableButton
        || submitSuccess
        || formState.values.applicant.currentLoans?.diff < 0
        || (
          !syncErrors
          && submitFailed
          && submitError?.id === data.messages.generalSubmitError.id
        ) || uncaughtRuntimeError
      }
      >
        {formatMessage(data.label)}
      </TeleButton>

      { submitError && (submitFailed || uncaughtRuntimeError) && !submitting && (
        <AlertWrapper>
          <Alert
            display={submitError.message}
            type={submitError.type}
          />
        </AlertWrapper>
      )}
    </div>
  );
};

SubmitButton.propTypes = {
  intl: intlShape.isRequired,
  data: PropTypes.shape().isRequired,
  submitSuccess: PropTypes.bool,
  disableButton: PropTypes.bool,
  setShowErrors: PropTypes.func,
};

SubmitButton.defaultProps = {
  submitSuccess: false,
  disableButton: false,
  setShowErrors: () => {},
};

const SubmitButtonWithIntl = injectIntl(SubmitButton);

export { SubmitButtonWithIntl as SubmitButton };

export default (SubmitButtonWithIntl);
